import i18n from 'i18next'

import { TRoutes } from './const'

/*
 * Die Voraussetzungen für die Aktivierung einer Junior Company erscheinen an
 * zwei Stellen: im Dashboard der Schüler:innen, bevor sie ihre Company zur
 * Prüfung einreichen, und in den Einstellungen der Administration, damit dort
 * nachvollziehbar ist, was die Schüler:innen zu sehen bekommen.
 *
 * Beide lesen dieselbe Liste. Stünde sie zweimal im Code, wären die Texte
 * spätestens beim nächsten Zusatz auseinandergelaufen — und die Vorschau in den
 * Einstellungen hätte etwas anderes behauptet als das Dashboard zeigt.
 *
 * Ob eine Voraussetzung erfüllt ist, entscheidet das Backend
 * (`activationsStatus` aus dem Dashboard). Ohne diesen Stand bleibt `status`
 * undefined — die Vorschau der Administration zeigt die Liste dann ohne Häkchen.
 *
 * Angesprochen werden Schüler:innen, nicht Studierende: „euer Unternehmen",
 * nicht „Ihre Studentenfirma". Die alten Texte kamen aus dem Englischen, wo
 * „students" beides heißt.
 *
 * Jeder Punkt trägt außerdem den Weg dorthin, wo er zu erfüllen ist. Ohne ihn
 * stand in der Liste, was fehlt, aber nicht, wohin.
 */

// Die Voraussetzungen in der Reihenfolge, in der die Schüler:innen sie sehen.
// Das Backend prüft sie fest verdrahtet; abschalten
// lassen sie sich trotzdem — dann fallen sie aus der Checkliste und damit aus
// der Bedingung zum Einreichen.
const BASE_REQUIREMENTS = [
	{
		id: 'riskAnalysis',
		title: 'Company profile & risk analysis completed',
		text: 'Your company profile and your risk analysis must be filled in completely',
		// Die Analyse ist ein Abschnitt des Unternehmensprofils; der Verweis
		// oeffnet ihn gleich mit.
		link: TRoutes.COMPANY_TAB('market'),
		linkLabel: 'To the risk analysis'
	},
	{
		id: 'employees',
		title: 'Minimum of 2 students as employees',
		text: 'Your company needs at least two registered students as employees',
		link: TRoutes.COMPANY_EMPLOYEE,
		linkLabel: 'To the employees'
	},
	{
		id: 'experts',
		title: 'Minimum of 1 business expert assigned',
		text: 'Your company needs at least one business expert',
		link: TRoutes.SUPPORTERS,
		linkLabel: 'To the business experts'
	},
	{
		id: 'parentalConfirmation',
		/*
		 * „hochgeladen **und geprüft**" — bis 31.08.2026 stand hier nur
		 * „hochgeladen". Das war falsch und hat gekostet: Gezählt wird der
		 * Prüfvermerk der Administration (`reviewedParentalConfirmation`,
		 * `services/companies/activation.js`), nicht die Datei. Eine Klasse, die
		 * alles hochgeladen hatte, sah einen Punkt, den sie erfüllt glaubte, und
		 * kam trotzdem nicht zum Einreichen — ohne dass irgendwo stand, worauf
		 * sie noch wartet. Das Backend beschriftete denselben Punkt seit jeher
		 * anders („… freigegeben", `REQUIREMENT_LABELS`); beide Enden sagen
		 * jetzt dasselbe.
		 */
		title: 'Parental/guardian consent form uploaded and checked',
		text: 'All parental consent forms must be available signed and checked by the administration',
		// Nicht ins eigene Profil: Die Voraussetzung gilt für *alle*
		// Mitarbeiter:innen, und nur die Mitarbeiter:innenliste zeigt auf einen
		// Blick, wessen Einverständniserklärung noch fehlt.
		link: TRoutes.COMPANY_EMPLOYEE,
		linkLabel: 'To the employees'
	},
	{
		id: 'isInvitedEmployeesCount',
		title: 'No employees remaining on the waiting list',
		text: 'All employees confirmed, pending invitations deleted',
		link: TRoutes.COMPANY_EMPLOYEE,
		linkLabel: 'To the employees'
	},
	{
		id: 'isWorkAreaAssigned',
		title: 'Working area assigned',
		text: 'Every employee must have been assigned a function',
		link: TRoutes.COMPANY_EMPLOYEE,
		linkLabel: 'To the employees'
	},
	{
		id: 'emailVerified',
		title: 'Company email address verified',
		text: 'The official email address of your company must be verified',
		link: TRoutes.COMPANY,
		linkLabel: 'To the company profile'
	},
	/*
	 * Lebensmittelhygiene und Allergeninformation (Anforderung #96).
	 *
	 * Steht am Ende der Liste und **nur bei den Junior Companies, die es
	 * betrifft**: Ob die Branche sie auslöst, rechnet der Server
	 * (`activationsStatus.hygieneTrainingApplies`) — die Oberfläche würde sonst
	 * dieselbe Frage ein zweites Mal beantworten, mit einer eigenen Liste von
	 * Branchen, die beim nächsten Zusatz auseinanderläuft.
	 */
	{
		id: 'hygieneTraining',
		title: 'Food hygiene and allergen information confirmed',
		text: 'Confirm how food hygiene and allergen information is covered in your company',
		link: TRoutes.COMPANY_TAB('business'),
		linkLabel: 'To the business activity'
	},
	/*
	 * Das Skills4Retail-Konzept — genau wie die Hygiene ein Punkt, der nicht
	 * für jede Junior Company gilt.
	 *
	 * Er hat seine eigene Einstellung (`conceptRequirement`), und dort steht je
	 * Unternehmensform, wo er sperrt: vor der Aktivierung, vor dem ersten
	 * Finanzbericht oder gar nicht. Nur im ersten Fall gehört er in diese
	 * Liste; ob das zutrifft, sagt der Server mit
	 * `activationsStatus.conceptApplies`.
	 *
	 * Bis 23.08.2026 fehlte die Zeile hier ganz, während der Server den Punkt
	 * längst mitmaß. Solange keine Unternehmensform auf „vor der Aktivierung"
	 * stand, fiel das nicht auf — sobald jemand umstellt, hätte die Checkliste
	 * alles abgehakt und das Einreichen wäre trotzdem abgewiesen worden.
	 */
	{
		id: 'concept',
		title: 'Skills4Retail concept completed and assessed',
		text: 'Your concept must be submitted and assessed by your teacher',
		link: TRoutes.CONCEPT,
		linkLabel: 'To the concept'
	}
]

// Kennungen aller abschaltbaren Voraussetzungen, in der Reihenfolge der
// Checkliste. Die Einstellungen bauen daraus ihre Schalterliste.
//
// Bis 31.10.2026 stand hier zusätzlich der Kursnachweis (`courseCertificate`,
// Einstellung `courseCertificateRequirement`): ein außerhalb gemachter Kurs,
// dessen Bestätigung hochgeladen werden musste. Seit die Kurse in der
// Anwendung selbst laufen, ist er ausgebaut; bereits hochgeladene Nachweise
// bleiben als Dokumente stehen.
export const REQUIREMENT_IDS = BASE_REQUIREMENTS.filter(item => item.id !== 'concept').map(item => item.id)

/**
 * Ist eine feste Voraussetzung eingeschaltet?
 *
 * Fehlt der Eintrag — und das ist der Normalfall, solange die Einstellung nie
 * gespeichert wurde —, gilt sie als eingeschaltet. Ein fehlender Wert darf
 * keine Bedingung stillschweigend fallen lassen.
 */
export const isRequirementActive = (options, id) => options?.activationRequirements?.[id] !== false

/**
 * Alle Voraussetzungen — auch die abgeschalteten — in der Reihenfolge der
 * Checkliste, jeweils mit Schalterstand.
 *
 * Die Einstellungen brauchen diese Sicht: In der Vorschau steht, was die
 * Schüler:innen sehen; in der Konfiguration darunter muss auch stehen, was sie
 * gerade nicht sehen, sonst gäbe es keinen Weg, es wieder einzuschalten.
 */
export const getRequirementCatalog = options => {
	/*
	 * Ohne das Konzept: Wo es sperrt, steht in seiner eigenen Einstellung
	 * (`conceptRequirement`, je Unternehmensform „vor der Aktivierung", „vor
	 * dem ersten Finanzbericht" oder „aus"). Ein zweiter Schalter hier wäre ein
	 * zweiter Ort für dieselbe Frage — und der eine würde den anderen
	 * überstimmen, ohne dass es jemand sähe.
	 */
	const rows = BASE_REQUIREMENTS.filter(requirement => requirement.id !== 'concept').map(requirement => ({
		id: requirement.id,
		title: i18n.t(requirement.title),
		text: i18n.t(requirement.text),
		active: isRequirementActive(options, requirement.id)
	}))

	return rows
}

/**
 * Die Liste, wie eine Schüler:in sie sieht.
 *
 * @param options            Die Options aus dem Store (Schalter).
 * @param activationsStatus  Erfüllt-Stand aus dem Dashboard.
 */
export const getActivationRequirements = ({
	options,
	activationsStatus = {},
	hygienePreview = false,
	conceptPreview = false
} = {}) => {
	/*
	 * Die Hygiene-Bestätigung ist der einzige Punkt, der nicht für jede Junior
	 * Company gilt. Ohne Auskunft des Servers bleibt sie weg — in der Vorschau
	 * der Administration wird sie ausdrücklich angefordert (`hygienePreview`),
	 * sonst gäbe es dort keinen Ort, an dem sie je zu sehen wäre.
	 */
	const zeigtHygiene = hygienePreview || activationsStatus.hygieneTrainingApplies === true
	// Dasselbe für das Skills4Retail-Konzept: Es steht nur da, wo es vor der
	// Aktivierung sperrt (`conceptApplies`) — in der Vorschau der Administration
	// ausdrücklich angefordert.
	const zeigtKonzept = conceptPreview || activationsStatus.conceptApplies === true

	/*
	 * Punkte, die diese Junior Company nur deshalb als erfüllt führt, weil sie
	 * erst **nach** ihrer Aktivierung verlangt wurden (Regel 3 der
	 * Einstellungsänderungen, siehe backend services/options). Sie stehen
	 * grün in der Liste — der Zusatz sagt, warum, damit niemand einen Haken für
	 * eine erbrachte Leistung hält.
	 */
	const nachtraeglich = new Set(activationsStatus.grandfathered || [])

	const gilt = requirement => {
		if (requirement.id === 'hygieneTraining') return zeigtHygiene
		if (requirement.id === 'concept') return zeigtKonzept

		return true
	}

	return BASE_REQUIREMENTS.filter(
		requirement => isRequirementActive(options, requirement.id) && gilt(requirement)
	).map(requirement => ({
		id: requirement.id,
		title: i18n.t(requirement.title),
		text: i18n.t(requirement.text),
		link: requirement.link,
		linkLabel: i18n.t(requirement.linkLabel),
		status: activationsStatus[requirement.id],
		grandfathered: nachtraeglich.has(requirement.id)
	}))
}
